import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import da from 'date-fns/locale/da';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import PlacesAutocomplete, { PlaceResult } from './PlacesAutocomplete';

// Minimal cargo info needed for the split dialog
// Works with both KoreseddelCargo and RouteCargoSummary
export interface SplitableCargoInfo {
  id: string;
  ref: string;
  customerName: string | null;
  firstStop: { city: string | null };
  lastStop: { city: string | null } | null;
}

interface SplitCargoDialogProps {
  open: boolean;
  onClose: () => void;
  cargo: SplitableCargoInfo | null;
  onSplit: (cargoId: string, reloadAddress: PlaceResult, reloadDate: string) => Promise<void>;
  isLoading?: boolean;
}

export default function SplitCargoDialog({
  open,
  onClose,
  cargo,
  onSplit,
  isLoading,
}: SplitCargoDialogProps) {
  const [reloadAddress, setReloadAddress] = useState<PlaceResult | null>(null);
  const [reloadDate, setReloadDate] = useState<Date | null>(new Date());
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setReloadAddress(null);
    setReloadDate(new Date());
    setError(null);
    onClose();
  };

  const handleSplit = async () => {
    if (!cargo || !reloadAddress || !reloadDate) return;

    if (!reloadAddress.address || !reloadAddress.city) {
      setError('Indtast en gyldig adresse med by');
      return;
    }

    setError(null);
    try {
      const dateStr = reloadDate.toISOString().split('T')[0];
      await onSplit(cargo.id, reloadAddress, dateStr);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Der opstod en fejl');
    }
  };

  const fromCity = cargo?.firstStop?.city || 'Ukendt';
  const toCity = cargo?.lastStop?.city || 'Ukendt';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CallSplitIcon />
          Split ordre
        </DialogTitle>
        <DialogContent>
          {cargo && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Original order info */}
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Original ordre
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {cargo.ref}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {fromCity} → {toCity}
                </Typography>
                {cargo.customerName && (
                  <Typography variant="body2" color="text.secondary">
                    Kunde: {cargo.customerName}
                  </Typography>
                )}
              </Box>

              <Divider />

              {/* Explanation */}
              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  <strong>1/3:</strong> {fromCity} → {toCity} · <strong>2/3:</strong> {fromCity} → Omlæsning · <strong>3/3:</strong> Omlæsning → {toCity}
                </Typography>
              </Alert>

              {/* Reload address input */}
              <PlacesAutocomplete
                value={reloadAddress}
                onChange={setReloadAddress}
                error={!!error && !reloadAddress}
              />

              {/* Reload date picker */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Omlæsningsdato
                </Typography>
                <DatePicker
                  value={reloadDate}
                  onChange={setReloadDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>

            {/* Show selected address details */}
            {reloadAddress && reloadAddress.city && (
              <Box sx={{ bgcolor: 'success.50', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                <Typography variant="subtitle2" color="success.dark" gutterBottom>
                  Valgt omlæsningssted
                </Typography>
                <Typography variant="body2">
                  <strong>Adresse:</strong> {reloadAddress.address}
                </Typography>
                <Typography variant="body2">
                  <strong>By:</strong> {reloadAddress.postcode} {reloadAddress.city}
                </Typography>
                <Typography variant="body2">
                  <strong>Land:</strong> {reloadAddress.country}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error">{error}</Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Annuller
        </Button>
        <Button
          variant="contained"
          onClick={handleSplit}
          disabled={isLoading || !reloadAddress || !reloadAddress.city || !reloadDate}
          startIcon={isLoading ? <CircularProgress size={16} /> : <CallSplitIcon />}
        >
          {isLoading ? 'Splitter...' : 'Split ordre'}
        </Button>
      </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
